import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { useMemo, useEffect } from 'react';
import { bindActionCreators } from 'redux';
import { loadSubscriptions } from '../redux/actions';
/**
 * The `useSubscriptions` hook returns an object representing the current user's
 * subscriptions.
 *
 * This hook has a side effect that will load all the user's subscriptions upon
 * calling it, if not already loaded.
 *
 * @example
 *
 * ```javascript
 * import { useSubscriptions } from '@chordcommerce/react-autonomy'
 *
 * const SubscriptionsPage = () => {
 *   const { subscriptions, isLoaded, isFetching, error } = useSubscriptions()
 *
 *   if (isFetching) {
 *     return <p>Loading…</p>
 *   }
 *
 *   return (
 *     <dl>
 *       {subscriptions.map(subscription => (
 *         <React.Fragment key={subscription.id}>
 *           <dt>Subscription #{subscription.id}</dt>
 *           <dd>
 *             Renews every {subscription.intervalLength} {subscription.intervalUnit}
 *             <br />
 *             (Items: {subscription.lineItems.map(li => li.sku).join(', ')})
 *           </dd>
 *         </React.Fragment>
 *       )}
 *     </dl>
 *   )
 * }
 * ```
 *
 * @category Hook
 */
export function useSubscriptions() {
  const subscriptionsState = useSelector(state => state.subscriptions, shallowEqual);
  const isFetching = !!subscriptionsState.loading?.includes('list');
  const isLoaded = !!subscriptionsState.success?.includes('list');
  const error = subscriptionsState.error?.list;
  const dispatch = useDispatch();
  const actionDispatches = useMemo(() => bindActionCreators({
    loadSubscriptions
  }, dispatch), [dispatch]);
  useEffect(() => {
    if (isFetching || isLoaded || error) return;
    if (Object.keys(subscriptionsState.data).length === 0) {
      actionDispatches.loadSubscriptions();
    }
  }, [subscriptionsState]);
  const flatSubscriptions = useMemo(() => Object.values(subscriptionsState.data), [subscriptionsState.data]);
  return {
    subscriptions: flatSubscriptions,
    isLoaded,
    isFetching,
    error,
    ...actionDispatches
  };
}